import { ensureServiceToken, rejectServiceToken } from './kiosk-service-token';

/**
 * KIOSK API client。
 *
 * 自助購票機為公開機台 —— products / schedules / recommendations / orders /
 * payment-callback 端點皆不需認證（後端無 guard）。EDC 付款、票券 QR、雲端語音端點
 * 使用服務帳號 token（`ensureServiceToken` / `authenticatedApiFetch`）。
 * EDC 呼叫與結果恢復放在 edc-api.ts。
 *
 * 服務帳號本身不在這個檔案：正式機台透過本機服務（`kiosk-account-plugin.ts`）取得
 * 短期 token，帳密不放前端 bundle，見 `./kiosk-service-token`。
 */

/**
 * API 位址。`VITE_API_BASE` 優先；沒設就依**瀏覽器目前的主機名**組出來。
 *
 * 為什麼不是寫死 `localhost:3000`：後端是多租戶，由 Host 標頭決定是哪一家公司。
 * 位址寫死的話，不論從 `localhost:5175` 還是 `royal.localhost:5175` 進來，送出去的
 * Host 都是 `localhost` —— 擺在 A 公司大廳的機台會賣出 B 公司的船票，而且不會有
 * 任何錯誤訊息。機台是無人看管的，這種錯不會有人當場發現。
 *
 * port 固定 3000（dev 的 API port）；正式環境機台指向的位址由佈署時的
 * `VITE_API_BASE` 決定。
 */
function defaultApiBase(): string {
  if (typeof window === 'undefined') return 'http://localhost:3000/api/v1';
  return `${window.location.protocol}//${window.location.hostname}:3000/api/v1`;
}

export const API_BASE: string = import.meta.env.VITE_API_BASE ?? defaultApiBase();

/** 心跳回報間隔（毫秒）；與後端 sweeper 離線閾值搭配（後端 30s / 離線 90s） */
export const KIOSK_HEARTBEAT_INTERVAL_MS = 30_000;

// ---------- 型別（與後端 DTO 對齊；子集自 apps/pwa api-client）----------

export interface ProblemDetails {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  [key: string]: unknown;
}

export interface ProductSummary {
  /** API base 相對路徑；推薦流程組合的簡略摘要可省略。 */
  cover_media_url?: string | null;
  cover_media_id?: string | null;
  id: string;
  sku: string;
  category: string;
  name_i18n: Record<string, string>;
  description_i18n: Record<string, string> | null;
  /** numeric string from PostgreSQL */
  base_price: string;
  currency: string;
  duration_minutes: number | null;
  channels: string[];
  is_published: boolean;
}
export interface ProductListResponse {
  items: ProductSummary[];
  total: number;
  limit: number;
  offset: number;
}

export interface ScheduleSummary {
  id: string;
  product_id: string;
  departure_at: string;
  departure_end_at: string;
  capacity: number;
  reserved_count: number;
  weather_cancelled: boolean;
  /** 停售時刻（開船前 N 分鐘）；只供顯示，閘門在伺服器。舊 API 沒有這個欄位。 */
  sales_close_at?: string;
}
export interface ScheduleListResponse {
  items: ScheduleSummary[];
}

export type KioskTicketType =
  | 'adult'
  | 'senior'
  | 'child'
  | 'infant'
  | 'early_bird'
  | 'package_family'
  | 'custom';

export interface CreateOrderItem {
  product_id: string;
  schedule_id: string;
  quantity: number;
  unit_price: number | string;
  /** 票種；後端依此解析對應 pricing 規則（成人/兒童）。 */
  ticket_type?: KioskTicketType;
  /**
   * 這筆是哪張套票的一段；單買時省略。
   *
   * 套票不建班次——顧客為每一段各挑一個時段，同一張套票的每一段都帶同一個值。
   * 後端據此驗證組合完整、時段不重疊，並把打包價分攤到各段。
   */
  package_product_id?: string;
}
export interface CreateOrderRequest {
  is_hotel_guest?: boolean;
  ticket_delivery_method?: 'paper-qr' | 'email' | 'sms' | 'online';
  /** 公開建單端點只收官網與自助機（2026-10-19） */
  channel: 'web' | 'kiosk';
  subtotal: number | string;
  total: number | string;
  items: CreateOrderItem[];
  /**
   * 選填（2026-10-30）：店家只開 email 通知、統編發票、Email 收票時才必填；留空就不送。
   * 不符合店家的通知渠道時後端回 400 `BIZ_CONTACT_REQUIRED`。
   */
  buyer_email?: string;
  /** 手機：台灣手機（後端正規化成 09xxxxxxxx）或任何國家的 E.164；機台畫面上必填 */
  buyer_phone?: string;
  /** 顧客在機台上選的介面語系；顧客通知依此選語言 */
  locale?: 'zh-TW' | 'en' | 'ja';
  /** 發票開立方式；省略時由後端決定預設（見 `CreateOrderInvoice`）。 */
  invoice?: CreateOrderInvoice;
  /** 顧客實際同意的購票須知版號；沒有取得同意時不送。 */
  purchase_notice_version?: number;
}
/**
 * 建單時的發票選擇（POST /orders 的 `invoice`）。
 *
 * 規則由後端驗（不合法回 400 `INVOICE_REQUEST_INVALID`），機台只是先擋明顯的錯：
 * - `business`（統編）的開立通知寄到 `buyer_email`，所以選統編時 email 必填。
 * - `donation` 不帶 `donation_code` → 後端用 CMS 設定的預設受贈單位。
 * - `paper` 開紙本，標成「待列印」；機台列印證明聯尚未實作，由櫃台補印。
 * - `member_carrier` 使用鯨躍會員載具，須提供買方 Email，不列印紙本發票。
 */
export interface CreateOrderInvoice {
  paper_requested?: boolean;
  kind: 'member_carrier' | 'mobile_barcode' | 'donation' | 'business' | 'paper';
  mobile_barcode?: string;
  donation_code?: string;
  tax_id?: string;
  company_name?: string;
}
/** GET /invoices/donation-target：CMS 設定的預設捐贈單位；沒設定時兩者皆為 null。 */
export interface DonationTarget {
  code: string | null;
  name: string | null;
}
export type OrderState =
  | 'HOLD'
  | 'PAYMENT_PENDING'
  | 'PAID'
  | 'EXPIRED'
  | 'CANCELLED'
  | 'PAYMENT_FAILED'
  | 'OVERSOLD';
export interface OrderSummary {
  id: string;
  order_number?: string;
  state: OrderState;
  subtotal: string;
  total: string;
  hold_expires_at?: string;
}
export interface PaymentCallbackResponse {
  outcome: 'paid' | 'oversold' | 'failed';
  order: OrderSummary;
  issuedTicketIds?: string[];
}

export interface RecommendRequest {
  stay_minutes: number;
  budget?: number | null;
  party_adults: number;
  party_children?: number;
  interests?: string[];
  /**
   * 出發日期 `YYYY-MM-DD`（Asia/Taipei）。
   *
   * 給了就只推薦當天真的有位子的商品（未停航、剩餘座位夠這一團）；
   * 不給則不看班次，維持語音入口等既有呼叫端的行為。
   */
  date?: string;
}
export interface RecommendProduct {
  product_id: string;
  sku: string;
  name_i18n: Record<string, string>;
  category: string;
  duration_minutes: number;
  base_price: string;
}
export interface RecommendItinerary {
  id: string;
  products: RecommendProduct[];
  estimated_minutes: number;
  total_price: string;
  fit_note: string;
}
export interface RecommendResponse {
  party_size: number;
  stay_minutes: number;
  budget: number | null;
  recommendations: RecommendItinerary[];
}

export type TicketState = 'valid' | 'used' | 'void_pending_refund' | 'voided' | 'expired';
export interface TicketQr {
  ticket_id: string;
  ticket_code: string;
  ticket_state: TicketState;
  refund_risk_class: 'REFUND_ELIGIBLE' | 'REFUND_LOCKED';
  /** compact JWS — 直接給 QR encoder */
  qr_payload: string;
}

// ---------- fetch wrapper ----------

export class ApiError extends Error {
  override readonly name = 'ApiError';
  readonly status: number;
  readonly problem?: ProblemDetails;
  constructor(status: number, message: string, problem?: ProblemDetails) {
    super(message);
    this.status = status;
    if (problem !== undefined) this.problem = problem;
  }
}

interface FetchInit extends Omit<RequestInit, 'body'> {
  body?: unknown;
  token?: string;
}

export async function apiFetch<T = unknown>(path: string, init: FetchInit = {}): Promise<T> {
  const { body, token, headers: initHeaders, ...rest } = init;
  const headers = new Headers(initHeaders);
  if (body !== undefined && !(body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (!headers.has('Accept')) headers.set('Accept', 'application/json');
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const requestInit: RequestInit = { ...rest, headers, credentials: 'include' };
  if (body !== undefined) {
    requestInit.body =
      body instanceof FormData || typeof body === 'string' ? body : JSON.stringify(body);
  }

  const res = await fetch(`${API_BASE}${path}`, requestInit);
  if (!res.ok) {
    let problem: ProblemDetails | undefined;
    if ((res.headers.get('content-type') ?? '').includes('json')) {
      try {
        problem = (await res.json()) as ProblemDetails;
      } catch {
        /* non-json error body */
      }
    }
    throw new ApiError(
      res.status,
      problem?.detail ??
        (typeof problem?.message === 'string' ? problem.message : undefined) ??
        problem?.title ??
        `HTTP ${res.status}`,
      problem,
    );
  }
  if (res.status === 204) return undefined as T;
  if (!(res.headers.get('content-type') ?? '').includes('json')) return undefined as T;
  return (await res.json()) as T;
}

/**
 * 一次購票流程的亂數前綴（Idempotency-Key 的前半）。
 *
 * ## ★ fallback 用 `getRandomValues`，不是 `Math.random()`
 *
 * `crypto.randomUUID` 是**限安全來源**的 API，所以機台跑在 http 上走的是 fallback——也就是
 * 實機上真正執行的那一條。原本那條是 `Date.now()` ＋
 * `Math.random().toString(36).slice(2, 10)`，約 41 位亂度。
 *
 * 兩台機台撞上的機率可以忽略，但**撞到不會報錯、會回放**：後端的冪等攔截器命中既有鍵，於是
 * B 機的顧客拿到 A 機的訂單。那跟「同一把鍵配不同內容」是同一類失效——靜默地給錯東西。
 * 「機率很低所以沒事」不足以守住那種後果。
 *
 * `getRandomValues` **不受**安全來源限制（只有 `randomUUID` 與 `crypto.subtle` 受限），所以
 * 兩條分支都拿得到密碼學亂度，跨機台碰撞變成結構上不可能。
 */
export function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined') {
    if (typeof crypto.randomUUID === 'function') return `kiosk-${crypto.randomUUID()}`;
    if (typeof crypto.getRandomValues === 'function') {
      const bytes = crypto.getRandomValues(new Uint8Array(16));
      return `kiosk-${Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')}`;
    }
  }
  /*
   * 連 `getRandomValues` 都沒有——任何真實瀏覽器都不會走到這裡（它從 IE11 就有）。留著只是
   * 為了不讓「沒有 crypto」變成一頁白畫面：弱的鍵比壞掉的購票流程好。
   */
  return `kiosk-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * 內容比對用的穩定序列化：物件的鍵**遞迴排序**，陣列順序**保留**（順序有意義）。
 *
 * ⚠️ 這支只用來比「這份內容跟上次送出的那份一樣嗎」，**不需要、也不該去跟後端的雜湊對齊**。
 * 後端比的是它自己算的 SHA-256，兩邊從不互相比對；硬要讓它們相等會變成一個必須同步演進的
 * 隱形契約。
 */
export function canonicalJson(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'null';
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(',')}]`;
  const entries = Object.entries(value as Record<string, unknown>)
    .filter(([, v]) => v !== undefined)
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${canonicalJson(v)}`).join(',')}}`;
}

/**
 * 取這份內容該用的 Idempotency-Key。**同一份內容永遠拿到同一把鍵；內容變了就配一把新的。**
 *
 * 規格只有一句話：**鍵決定身分，內容只負責不跟鍵矛盾。**
 *
 * |          | 同一把鍵 | 不同的鍵 |
 * | -------- | -------- | -------- |
 * | 內容相同 | 回放（重試）| 兩筆（兩位顧客買一樣的東西）|
 * | 內容不同 | 後端回 409 | 兩筆（正常）|
 *
 * ## 為什麼是一張表，而不是「只記上一次送出的那份」
 *
 * 顧客 P1 → 改成 P2 → 又改回 P1 時，只比最後一份會判定「跟 P2 不同」而再配一把新鍵，於是
 * P1 被送出兩次、配到兩把鍵——第一次若已經建單成功，就是**兩張內容一樣的訂單**。查表能讓
 * P1 拿回原本那把鍵。
 *
 * ## 為什麼鍵裡一定要有亂數（`nonce`）
 *
 * 純粹由內容決定會出事：同一台機器前後兩位顧客買一樣的東西（2 張成人、同一班）會算出同一把
 * 鍵，第二位顧客直接拿到第一位的訂單。而「同一班、2 張成人」大概就是機台上最常見的那一筆。
 * **任何用內容決定身分的設計都會壞在那裡。**
 *
 * ## 為什麼不雜湊
 *
 * 客戶端手上就有完整的前一份內容，直接比字串就好——零碰撞。用 32 位的非密碼學雜湊省不到
 * 什麼，而碰撞的後果正是這次要修的缺陷（兩份不同的內容算出同一把鍵 → 回放 → 拿到別人的單）。
 *
 * @param table 呼叫端用 `useRef` 持有（per-mount），不能用 state——否則讀到的是上一輪 render
 *              的值
 * @param nonce 這次流程的亂數前綴，由 {@link newIdempotencyKey} 產生
 */
export function idempotencyKeyFor(
  table: Map<string, string>,
  nonce: string,
  payload: unknown,
): string {
  const canonical = canonicalJson(payload);
  const existing = table.get(canonical);
  if (existing !== undefined) return existing;
  const key = `${nonce}-${table.size}`;
  table.set(canonical, key);
  return key;
}

// ---------- 公開端點（不需認證）----------

export interface KioskHeartbeatResponse {
  device_id: string;
  device_code: string;
  online: boolean;
  last_heartbeat: string;
}

/**
 * KIOSK 上線回報（POST /kiosk/heartbeat）。
 *
 * 公開端點；後端 upsert device_status(online=true, last_heartbeat=NOW())，
 * 供 CMS 設備儀表板（AC-H-3）顯示在線。device_code 未登錄 → 404（ApiError）。
 *
 * device_code 由呼叫端（useKioskHeartbeat）以 runtime 綁定值傳入；
 * 機台身分管理見 lib/device-identity。
 */
export function reportKioskHeartbeat(
  deviceCode: string,
  appVersion?: string,
): Promise<KioskHeartbeatResponse> {
  return apiFetch<KioskHeartbeatResponse>('/kiosk/heartbeat', {
    method: 'POST',
    body: appVersion
      ? { device_code: deviceCode, app_version: appVersion }
      : { device_code: deviceCode },
  });
}

/** 機台綁定前驗證 device_code（GET /kiosk/device-check）。公開端點。 */
export interface KioskDeviceCheck {
  device_code: string;
  exists: boolean;
  bindable: boolean;
  retired: boolean;
  device_type: string | null;
}
export function checkDevice(deviceCode: string): Promise<KioskDeviceCheck> {
  return apiFetch<KioskDeviceCheck>(
    `/kiosk/device-check?device_code=${encodeURIComponent(deviceCode)}`,
    { method: 'GET' },
  );
}

/**
 * 可售商品清單。
 *
 * `category` 對應 products.category（trip 航程／experience 水上體驗／
 * package 套裝／charter 包船）；不給則不過濾。過濾在後端做，機台不必把
 * 全部商品抓下來再自己篩。
 */
export function listProducts(category?: string): Promise<ProductListResponse> {
  const q = new URLSearchParams({
    // CMS 的「銷售通路」設定要真的管得到機台：沒帶這個參數的話，
    // 標成僅限官網的商品照樣會出現在購票機上，那個欄位就形同虛設
    channel: 'kiosk',
    // 未上架的商品不該離開後端。原本是抓回全部再由畫面自己濾掉，
    // 等於把還沒公開的定價與名稱送到公開機台上
    is_published: 'true',
  });
  if (category !== undefined && category !== '') q.set('category', category);
  return apiFetch<ProductListResponse>(`/products?${q.toString()}`, { method: 'GET' });
}

/**
 * 某商品可售的班次（依出發時間遞增）。
 *
 * 只取此刻之後的：已開航的班次不該出現在購票機上。預設 limit 是 20，對「一天
 * 五班、排兩週」來說只夠看四天，故放寬到 60（約兩週的量）。
 */
export function listSchedules(productId: string): Promise<ScheduleListResponse> {
  const qs = new URLSearchParams({
    product_id: productId,
    departure_after: new Date().toISOString(),
    limit: '60',
  });
  return apiFetch<ScheduleListResponse>(`/schedules?${qs.toString()}`, { method: 'GET' });
}

/** 套票的一段（GET /products/:id/components）。 */
export interface PackageComponent {
  component_product_id: string;
  sort_order: number;
  sku: string;
  name_i18n: Record<string, string>;
  description_i18n: Record<string, string> | null;
  duration_minutes: number | null;
  base_price: string;
  cover_media_id: string | null;
}

/**
 * 套票含哪些商品（依呈現順序）。
 *
 * 套票商品不建班次，機台拿到組成後再逐段查各自的班次讓顧客挑。回空陣列代表這張
 * 套票還沒設定組成——那時它不可售，後端下單也會擋。
 */
export function listProductComponents(productId: string): Promise<PackageComponent[]> {
  return apiFetch<PackageComponent[]>(`/products/${encodeURIComponent(productId)}/components`, {
    method: 'GET',
  });
}

/** F-3 景點介紹（GET /spots 為公開端點；KIOSK 前端過濾 is_published 取已發布）。 */
export interface SpotSummary {
  id: string;
  spot_code: string;
  name_i18n: Record<string, string>;
  description_i18n: Record<string, string> | null;
  latitude: string;
  longitude: string;
  trigger_radius_m: number;
  is_published: boolean;
}
export function listSpots(): Promise<SpotSummary[]> {
  return apiFetch<SpotSummary[]>('/spots', { method: 'GET' });
}

export function getRecommendations(payload: RecommendRequest): Promise<RecommendResponse> {
  return apiFetch<RecommendResponse>('/recommendations', { method: 'POST', body: payload });
}

/**
 * POST /pricing/quote — read-only 折後 unit_price 預覽。
 *
 * KIOSK 在 Schedule 選完班次 + 人數後呼叫；後續 Delivery / Invoice / Payment
 * 螢幕統一用回傳之 `total` 顯示，避免「點選 1500、收據 1200」之 UX 不一致。
 * 同一個 PricingResolver 與 OrdersService.create 共用，最終落庫值一致。
 */
export interface PricingQuoteTicket {
  ticket_type: KioskTicketType;
  quantity: number;
}
export interface PricingQuoteRequest {
  is_hotel_guest?: boolean;
  product_id: string;
  schedule_id: string;
  channel: 'web' | 'kiosk' | 'partner' | 'floor' | 'recovery';
  /** 各票種與數量；成人/兒童各自一行，後端分票種解析。 */
  tickets: PricingQuoteTicket[];
}
export interface PricingQuoteLine {
  ticket_type: string;
  quantity: number;
  unit_price: string;
  /** 折扣前原價（劃線價）；> unit_price 表示有促銷。 */
  original_price: string;
  line_total: string;
  pricing_id: string | null;
  rule_summary: string | null;
}
export interface PricingQuoteResponse {
  lines: PricingQuoteLine[];
  party_size: number;
  total: string;
}
export function getPricingQuote(payload: PricingQuoteRequest): Promise<PricingQuoteResponse> {
  return apiFetch<PricingQuoteResponse>('/pricing/quote', { method: 'POST', body: payload });
}

/** 可售票種 + 各票種基本單價（GET /pricing/ticket-types）。 */
export interface SellableTicketType {
  ticket_type: string;
  display_name_i18n: Record<string, string> | null;
  unit_price: string;
  /** 折扣前原價（劃線價）；> unit_price 表示有促銷。 */
  original_price: string;
  pricing_id: string | null;
  rule_summary: string | null;
}
/**
 * 可售票種 + 各票種單價。
 *
 * 套票不建班次，故 `schedule_id` 可省略——省略時後端只回商品價目表，不套 pricing
 * 規則（規則掛在 (product, schedule) 上，對套票沒有可匹配的對象）。
 */
export function getTicketTypes(params: {
  is_hotel_guest?: boolean;
  product_id: string;
  schedule_id?: string;
  channel: string;
}): Promise<{ ticket_types: SellableTicketType[] }> {
  const qs = new URLSearchParams({
    product_id: params.product_id,
    ...(params.is_hotel_guest === undefined
      ? {}
      : { is_hotel_guest: String(params.is_hotel_guest) }),
    channel: params.channel,
    ...(params.schedule_id === undefined ? {} : { schedule_id: params.schedule_id }),
  });
  return apiFetch<{ ticket_types: SellableTicketType[] }>(
    `/pricing/ticket-types?${qs.toString()}`,
    {
      method: 'GET',
    },
  );
}

export function createOrder(
  payload: CreateOrderRequest,
  idempotencyKey: string,
): Promise<OrderSummary> {
  return apiFetch<OrderSummary>('/orders', {
    method: 'POST',
    body: payload,
    headers: { 'Idempotency-Key': idempotencyKey },
  });
}

/** 發票捐贈的預設單位（公開端點）；捐贈選項要寫出捐給誰，沒設定就停用。 */
export function getDonationTarget(): Promise<DonationTarget> {
  return apiFetch<DonationTarget>('/invoices/donation-target', { method: 'GET' });
}

/** HOLD → PAYMENT_PENDING。 */
export function initiatePayment(orderId: string): Promise<{ order: OrderSummary }> {
  return apiFetch<{ order: OrderSummary }>(`/orders/${encodeURIComponent(orderId)}/events`, {
    method: 'POST',
    body: { event: 'initiate_payment' },
    signal: AbortSignal.timeout(10_000),
  });
}

// ---------- 服務帳號（EDC／票券 QR／語音）----------
//
// `paymentCallback`（PAYMENT_PENDING → PAID）已移除：那是舊的 mock PSP 流程遺留
// （`mockProviderTxId()`），本分支已經換成真的 EDC 刷卡（`edc-api.ts` 的
// `startEdcPayment` / `edcStatus`，由後端在刷卡成功時同一個 tx 內轉 PAID 並發票）。
// 唯一呼叫過它的 `PaymentProgress.tsx` 已經不再需要它。

/** Retry only a rejected 401, never timeouts or already accepted paid work. */
export async function authenticatedApiFetch<T = unknown>(
  path: string,
  init: FetchInit = {},
): Promise<T> {
  init.signal?.throwIfAborted();
  const token = await ensureServiceToken();
  init.signal?.throwIfAborted();
  if (!token) throw new ApiError(401, '機台登入失敗', { code: 'VOICE_AUTH_REQUIRED' });
  try {
    return await apiFetch<T>(path, { ...init, token });
  } catch (error) {
    if (!(error instanceof ApiError) || error.status !== 401) throw error;
    // Keep another request's in-flight refresh instead of issuing duplicate logins.
    await rejectServiceToken(token);
    const refreshed = await ensureServiceToken();
    init.signal?.throwIfAborted();
    if (!refreshed) throw new ApiError(401, '機台登入失敗', { code: 'VOICE_AUTH_REQUIRED' });
    try {
      return await apiFetch<T>(path, { ...init, token: refreshed });
    } catch (retryError) {
      if (retryError instanceof ApiError && retryError.status === 401) {
        throw new ApiError(401, '機台登入失敗', { code: 'VOICE_AUTH_REQUIRED' });
      }
      throw retryError;
    }
  }
}
/**
 * 拉票券 QR（出票後列印 / 螢幕顯示用）。服務帳號無權或登入失敗時回 null。
 */
export async function fetchTicketQr(ticketId: string): Promise<TicketQr | null> {
  try {
    return await authenticatedApiFetch<TicketQr>(`/tickets/${encodeURIComponent(ticketId)}/qr`, {
      method: 'GET',
    });
  } catch {
    return null;
  }
}
